import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress,
  Divider, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import { InfoOutlined, Save } from '@mui/icons-material';
import { adminApi } from '../../api/adminApi';

export default function SalesSettingsPage() {
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [settings, setSettings] = useState({
    salesEmail: '',
    salesPhone: '',
    salesUrl:   '',
    updatedAt:  null,
    updatedBy:  null,
  });
  const [errors, setErrors] = useState({});
  const [toast, setToast]   = useState({ open: false, msg: '', severity: 'success' });

  useEffect(() => {
    (async () => {
      try {
        const data = await adminApi.getSalesSettings();
        setSettings(data);
      } catch (e) {
        showToast(`Failed to load settings: ${e.message}`, 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function showToast(msg, severity = 'success') {
    setToast({ open: true, msg, severity });
  }

  function validate() {
    const errs = {};
    if (!settings.salesEmail || !settings.salesEmail.trim()) {
      errs.salesEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(settings.salesEmail.trim())) {
      errs.salesEmail = 'Must be a valid email address';
    }
    if (settings.salesUrl && settings.salesUrl.trim()) {
      try {
        new URL(settings.salesUrl.trim());
      } catch {
        errs.salesUrl = 'Must be a valid URL (e.g. https://calendly.com/your-team)';
      }
    }
    return errs;
  }

  async function handleSave() {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const updated = await adminApi.updateSalesSettings({
        salesEmail: settings.salesEmail.trim(),
        salesPhone: settings.salesPhone.trim(),
        salesUrl:   settings.salesUrl.trim(),
      });
      setSettings(updated);
      showToast('Sales settings saved.');
    } catch (e) {
      showToast(`Save failed: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <Box display="flex" justifyContent="center" pt={8}><CircularProgress /></Box>;
  }

  return (
    <Box maxWidth={680} mx="auto" px={2} py={4}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Sales Settings</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Configure sales contact information shown on &quot;Contact Sales&quot; buttons
        throughout the tenant portal (e.g. the Add-Ons page).
      </Typography>

      <Stack spacing={3}>
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Contact information
            </Typography>

            <Stack spacing={2.5}>
              <TextField
                label="Sales email"
                fullWidth
                value={settings.salesEmail}
                onChange={(e) => {
                  setSettings((s) => ({ ...s, salesEmail: e.target.value }));
                  setErrors((er) => ({ ...er, salesEmail: undefined }));
                }}
                error={!!errors.salesEmail}
                helperText={errors.salesEmail || 'Primary email for sales inquiries'}
                placeholder="sales@yourcompany.com"
              />

              <TextField
                label="Sales phone (optional)"
                fullWidth
                value={settings.salesPhone}
                onChange={(e) => {
                  setSettings((s) => ({ ...s, salesPhone: e.target.value }));
                }}
                helperText="Phone number shown alongside email"
                placeholder="+1 (555) 123-4567"
              />

              <TextField
                label="Sales URL (optional)"
                fullWidth
                value={settings.salesUrl}
                onChange={(e) => {
                  setSettings((s) => ({ ...s, salesUrl: e.target.value }));
                  setErrors((er) => ({ ...er, salesUrl: undefined }));
                }}
                error={!!errors.salesUrl}
                helperText={errors.salesUrl || 'Link to a booking page, form, or sales landing page'}
                placeholder="https://calendly.com/your-team"
              />
            </Stack>

            <Alert severity="info" icon={<InfoOutlined />} sx={{ mt: 2.5, borderRadius: 1.5 }}>
              This information appears on the tenant Add-Ons page when a feature
              requires a custom plan. Tenants will see a &quot;Contact Sales&quot; button
              that links to the email address configured here.
            </Alert>
          </CardContent>
        </Card>

        {settings.updatedAt && (
          <Typography variant="caption" color="text.secondary">
            Last updated {new Date(settings.updatedAt).toLocaleString()}
            {settings.updatedBy && ` by ${settings.updatedBy}`}
          </Typography>
        )}

        <Divider />

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            variant="contained"
            size="large"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={saving}
            sx={{ borderRadius: 2, px: 4 }}
          >
            Save settings
          </Button>
        </Stack>
      </Stack>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
